// /src/pages/ModulesPage.js
// Admin screen for the module catalog (the rows behind the sidebar).
// "Licensed" is what this deployment has bought; who may use a licensed
// module is decided separately by grants on the Users and Departments pages.
import React, { useEffect, useState } from "react";

function api(path, opts = {}) {
  const token = localStorage.getItem("token");
  return fetch(`/api${path}`, {
    method: opts.method || "GET",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  }).then(async (res) => {
    if (!res.ok) {
      const raw = await res.text();
      let msg = raw;
      try { msg = JSON.parse(raw).error || raw; } catch { /* keep raw text */ }
      throw new Error(msg || `${res.status} ${res.statusText}`);
    }
    return res.status === 204 ? null : res.json();
  });
}

export default function ModulesPage() {
  const [modules, setModules] = useState([]);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  async function load() {
    try {
      setLoading(true);
      setErr("");
      setModules((await api(`/modules`)) || []);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function update(id, patch) {
    try {
      setErr("");
      await api(`/modules/${id}`, { method: "PUT", body: patch });
      load();
    } catch (e) {
      setErr(e.message);
      load();   // resync so the row shows what the server actually holds
    }
  }

  async function create(e) {
    e.preventDefault();
    const f = new FormData(e.target);
    try {
      setErr("");
      await api(`/modules`, {
        method: "POST",
        body: {
          key: (f.get("key") || "").trim(),
          name: (f.get("name") || "").trim(),
          path: (f.get("path") || "").trim(),
          status: f.get("status") || "active",
          sort_order: Number(f.get("sort_order") || 100),
        },
      });
      e.target.reset();
      load();
    } catch (e2) {
      setErr(e2.message);
    }
  }

  return (
    <div style={{ padding: "16px" }}>
      <h2>Modules</h2>
      <p style={{ opacity: 0.75, marginTop: -6 }}>
        Unlicensed modules are hidden from every sidebar. Licensing a module does
        not by itself grant anyone access to it — assign that on the Users or
        Departments pages.
      </p>

      <details style={{ margin: "12px 0" }}>
        <summary style={{ cursor: "pointer" }}>Add module</summary>
        <form onSubmit={create} style={{ display: "grid", gap: 8, marginTop: 10, maxWidth: 420 }}>
          <input name="key" placeholder="key (stable identifier)" required style={inp} />
          <input name="name" placeholder="Display name" required style={inp} />
          <input name="path" placeholder="/route" required style={inp} />
          <select name="status" style={inp}>
            <option value="active">licensed</option>
            <option value="inactive">not licensed</option>
          </select>
          <input name="sort_order" type="number" defaultValue={100} style={inp} />
          <button style={btnPrimary}>Add</button>
        </form>
      </details>

      {err && <div style={{ color: "#f87171", marginBottom: 10 }}>{err}</div>}

      <div style={{ border: "1px solid #26324f", borderRadius: 8, overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#0c1326" }}>
              {["Name", "Key", "Path", "Licensed", "Order"].map((h) => (
                <th key={h} style={th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="5" style={td}>Loading…</td></tr>}
            {!loading && modules.length === 0 && (
              <tr><td colSpan="5" style={td}>No modules configured.</td></tr>
            )}
            {!loading && modules.map((m) => (
              <tr key={m.id}>
                <td style={td}>
                  <input
                    defaultValue={m.name}
                    onBlur={(e) => {
                      const v = e.target.value.trim();
                      if (v && v !== m.name) update(m.id, { name: v });
                    }}
                    style={cellInput}
                  />
                </td>
                <td style={{ ...td, opacity: 0.7 }}>{m.key}</td>
                <td style={td}>
                  <input
                    defaultValue={m.path}
                    onBlur={(e) => {
                      const v = e.target.value.trim();
                      if (v && v !== m.path) update(m.id, { path: v });
                    }}
                    style={cellInput}
                  />
                </td>
                <td style={td}>
                  <input
                    type="checkbox"
                    defaultChecked={m.status === "active"}
                    onChange={(e) =>
                      update(m.id, { status: e.target.checked ? "active" : "inactive" })
                    }
                  />
                </td>
                <td style={td}>
                  <input
                    type="number"
                    defaultValue={m.sort_order}
                    onBlur={(e) => {
                      const v = Number(e.target.value);
                      if (!Number.isNaN(v) && v !== m.sort_order) update(m.id, { sort_order: v });
                    }}
                    style={{ ...cellInput, width: 80 }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/*** Styles (matching UsersPage / DepartmentsPage) ***/
const inp = { padding:"8px 10px", borderRadius:8, border:"1px solid #334155", background:"#0f172a", color:"#e6eefc" };
const cellInput = { ...inp, padding:"4px 6px", width:"100%" };
const th = { textAlign:"left", padding:"10px 12px", borderBottom:"1px solid #26324f" };
const td = { padding:"10px 12px", borderBottom:"1px solid #1e293b" };
const btnPrimary = { padding:"8px 12px", background:"#2979ff", color:"#fff", border:"1px solid #2a6bff", borderRadius:8, cursor:"pointer" };
